import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import DocumentTile from '../../ingestion/ingestion-tile/tile/ArrIngestionTile';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './IngestionTile.scss';
import {IngestionProgress} from '../../../api/types/ingestionApi.type';
import {
  generateMetaDataList,
  formatDate,
  getStatusMessage,
} from '../utilities/Utilities';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrModal from '../../common/arr-modal/ArrModal';
import {
  useApproveIngestion,
  useDeleteIngestion,
  useRollbackIngestion,
} from '../../../hooks/ingestions/useIngestions';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import IngestionDetail from './details/IngestionDetail';

interface LoadMoreListProps {
  items: IngestionProgress[];
  initialCount?: number;
  step?: number;
  showAll: boolean;
  totalCount?: number;
  dataTestId: string;
}
const Ingestiontile: React.FC<LoadMoreListProps> = ({
  items,
  initialCount = 10,
  step = 5,
  showAll,
  totalCount = 0,
  dataTestId,
}) => {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [isIngestionOpen, setIsIngestionOpen] = useState(false);
  const [selctedIngestion, setSelectedIngestion] =
    useState<IngestionProgress | null>(null);
  const prevItemsLengthRef = useRef(totalCount);

  const handleIngestionClose = () => {
    setIsIngestionOpen(false);
    setSelectedIngestion(null);
  };

  const handleIngestionDetailOpen = (ingestion: IngestionProgress) => {
    setIsIngestionOpen(true);
    setSelectedIngestion(ingestion);
  };

  // Only reset visibleCount if items length actually changes
  useEffect(() => {
    if (totalCount !== prevItemsLengthRef.current) {
      prevItemsLengthRef.current = totalCount;

      if (showAll) {
        setVisibleCount(totalCount);
      } else {
        setVisibleCount(initialCount);
      }
    }
  }, [items, showAll, initialCount]);

  // Also respond to toggling showAll directly
  useEffect(() => {
    if (showAll) {
      setVisibleCount(totalCount);
    } else {
      setVisibleCount((prev) => Math.min(prev, initialCount));
    }
  }, [showAll, totalCount, initialCount]);

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + step, totalCount));
  };

  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<[ToastType]>([ToastType.Success]);

  const [modalConfig, setModalConfig] = useState<{
    open: boolean;
    action?: string;
    item?: any;
  }>({open: false});

  const {mutate: mutateRollback} = useRollbackIngestion();

  const {mutate: mutateDelete} = useDeleteIngestion();

  const {mutate: mutateApprove} = useApproveIngestion();

  const handleRollbackClick = () => {
    mutateRollback(
      {
        ingestionId: selctedIngestion?.ingestionId || 0,
        projectId: selctedIngestion?.projectId || 0,
      },
      {
        onSuccess: () => {
          setShowToast(true);
          setToastMessage(
            t('ingestions.ingestionActions.rollbackSuccessMessage')
          );
          setToastType([ToastType.Success]);
        },
        onError: (_) => {
          setShowToast(true);
          setToastMessage('');
          setToastType([ToastType.Error]);
        },
      }
    );
  };

  const handleDeleteClick = () => {
    mutateDelete(
      {
        ingestionId: selctedIngestion?.ingestionId || 0,
        projectId: selctedIngestion?.projectId || 0,
      },
      {
        onSuccess: () => {
          setShowToast(true);
          setToastMessage(
            t('ingestions.ingestionActions.deleteSuccessMessage')
          );
          setToastType([ToastType.Success]);
        },
        onError: (_) => {
          setShowToast(true);
          setToastMessage('');
          setToastType([ToastType.Error]);
        },
      }
    );
  };

  const handleApproveClick = () => {
    mutateApprove(
      {
        ingestionId: selctedIngestion?.ingestionId || 0,
        projectId: selctedIngestion?.projectId || 0,
      },
      {
        onSuccess: () => {
          setShowToast(true);
          setToastMessage(
            t('ingestions.ingestionActions.approveSuccessMessage')
          );
          setToastType([ToastType.Success]);
        },
        onError: (_) => {
          setShowToast(true);
          setToastMessage('');
          setToastType([ToastType.Error]);
        },
      }
    );
  };

  const getIngestionMenuItems = (item: IngestionProgress) => {
    return [
      {
        id: '1',
        label: 'Open in Wizard',
        onClick: () => onMenuItemClick('Open in Wizard', item),
        disabled: !item.canEdit,
      },
      {
        id: '2',
        label: t('ingestions.ingestionActions.rollback'),
        onClick: () =>
          onMenuItemClick(t('ingestions.ingestionActions.rollback'), item),
        disabled: !item.canRollback,
      },
      {
        id: '3',
        label: 'Copy',
        onClick: () => onMenuItemClick('Copy clicked', item),
        disabled: !item.canCopy,
      },
      {
        id: '4',
        label: t('ingestions.ingestionActions.delete'),
        onClick: () =>
          onMenuItemClick(t('ingestions.ingestionActions.delete'), item),
        disabled: !item.canDelete,
      },
    ];
  };

  const handleRollbackMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    setModalConfig({
      open: true,
      action: t('ingestions.ingestionActions.rollback'),
      item,
    });
  };

  const handleDeleteMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    setModalConfig({
      open: true,
      action: t('ingestions.ingestionActions.delete'),
      item,
    });
  };

  const handleApproveMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    setModalConfig({open: true, action: 'approve', item});
  };

  const handleCopyMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    if (item && item.canCopy && item.ingestionId) {
      navigate('/Ingestion/Copy/' + item.ingestionId);
    }
  };

  const handleOpenWizardMenuItemClick = (
    item: IngestionProgress | undefined | null
  ) => {
    if (item && item.canEdit && item.ingestionId) {
      navigate('/Ingestion/Edit/' + item.ingestionId);
    }
  };

  const onMenuItemClick = (selectedItem: string, item: IngestionProgress) => {
    setSelectedIngestion(item);
    switch (selectedItem) {
      case 'Open in Wizard':
        handleOpenWizardMenuItemClick(item);
        break;
      case t('ingestions.ingestionActions.rollback'):
        handleRollbackMenuItemClick(item);
        break;
      case 'Copy clicked':
        handleCopyMenuItemClick(item);
        break;
      case t('ingestions.ingestionActions.delete'):
        handleDeleteMenuItemClick(item);
        break;
      default:
        break;
    }
  };

  const clampPercent = (value?: number | null) => {
    if (value === null || value === undefined) {
      return 0;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return 0;
    }
    return Math.max(0, Math.min(100, Math.round(numeric)));
  };

  const hasMore = visibleCount < totalCount && !showAll;
  return (
    <div className="Ingetionwrapper" data-testid="ingestion-tile">
      <div
        className="IngetiontileContainer"
        data-testid={`ingestion-tile-div-${dataTestId}`}
      >
        {items.slice(0, visibleCount).map((item, index) => {
          const normalizedStatus = item.status?.toLowerCase();
          const completionPercent = clampPercent(item.percentageCompleted);
          if (normalizedStatus) {
            return (
              <div
                className="IngestiontileWrapper"
                key={item.ingestionId}
                data-testid={`ingestion-tile-div-tile-${dataTestId}-${index}`}
              >
                <DocumentTile
                  primaryLabel={item.name}
                  onPrimaryLabelClick={() => handleIngestionDetailOpen(item)}
                  secondaryLabel={item.projectName}
                  status={getStatusMessage(item.status)}
                  secondaryLabelColor="text-body-subtle"
                  lastModifiedBy={item.lastModifiedBy}
                  lastModifiedTimestamp={formatDate(item.lastModifiedOn)}
                  statusTimestamp={formatDate(item.completedOn)}
                  metaDataList={generateMetaDataList({
                    uploaded: item.docSourceCount,
                    released: item.docIngestedCount,
                    errors: item.docErrorCount,
                  })}
                  progressBarProps={{
                    height: 24,
                    percent: completionPercent,
                    state: getStatusMessage(item.status),
                  }}
                  actionMenuItems={getIngestionMenuItems(item)}
                  dataTestId={`ingestion-${index}`}
                />
              </div>
            );
          }
        })}
      </div>
      {totalCount > 0 && hasMore && (
        <div
          className="IngetionloadMoreContainer"
          data-testid={`ingestion-tile-load-more-${dataTestId}`}
        >
          <ArrButton
            onClick={loadMore}
            aria-label={t('ingestions.loadMore')}
            variant="secondary"
            size="small"
            dataTestId="ingestion-tile-load-more-button"
          >
            {t('ingestions.loadMore')}
          </ArrButton>
        </div>
      )}
      {items.length === 0 && (
        <div
          className="IngetionnoTiles"
          data-testid={`ingestion-tile-div-button`}
        ></div>
      )}
      {selctedIngestion && (
        <ArrDrawer
          isOpen={isIngestionOpen && selctedIngestion !== null}
          onClose={handleIngestionClose}
          ariaLabel="drawer content"
          size="large"
          side="right"
          showButton
          closeButtonAriaLabel={t('ingestions.details.closeButton')}
        >
          <IngestionDetail
            selectedIngestion={selctedIngestion}
            onRollbackClick={() =>
              handleRollbackMenuItemClick(selctedIngestion)
            }
            onCopyClick={() => handleCopyMenuItemClick(selctedIngestion)}
            onDeleteClick={() => handleDeleteMenuItemClick(selctedIngestion)}
            onEditClick={() => handleDeleteMenuItemClick(selctedIngestion)}
            onApproveClick={() => handleApproveMenuItemClick(selctedIngestion)}
          />
        </ArrDrawer>
      )}
      {modalConfig.open && (
        <ArrModal
          showModal={modalConfig.open}
          onClose={() => setModalConfig({open: false})}
          title={
            modalConfig.action === t('ingestions.ingestionActions.rollback')
              ? t('ingestions.ingestionActions.rollback')
              : modalConfig.action === t('ingestions.ingestionActions.delete')
                ? t('ingestions.ingestionActions.confirmDeleteTitle')
                : t('ingestions.ingestionActions.confirmApproveTitle')
          }
          description={
            modalConfig.action === t('ingestions.ingestionActions.rollback')
              ? t('ingestions.ingestionActions.confirmRollback')
              : t('ingestions.ingestionActions.confirmDelete')
          }
          dataTestId="ingestion-title-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setModalConfig({open: false}),
              variant: 'secondary',
              dataTestId: 'itm-cancel-btn',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: () => {
                if (
                  modalConfig.action ===
                  t('ingestions.ingestionActions.rollback')
                ) {
                  handleRollbackClick();
                } else if (
                  modalConfig.action === t('ingestions.ingestionActions.delete')
                ) {
                  handleDeleteClick();
                } else if (modalConfig.action === 'approve') {
                  handleApproveClick();
                }
                setModalConfig({open: false});
              },
              variant: 'primary',
              dataTestId: 'itm-ok-btn',
            },
          ]}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default Ingestiontile;
